const imageAccept = {
  'image/*': [
    '.avif',
    '.bmp',
    '.gif',
    '.heic',
    '.heif',
    '.jpeg',
    '.jpg',
    '.png',
    '.tif',
    '.tiff',
    '.webp',
  ],
};

export const IMAGE_ACCEPT = imageAccept;

export function isSupportedImageFile(file) {
  return Boolean(file?.type?.startsWith('image/'));
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Could not load image.'));
      image.src = String(reader.result);
    };
    reader.onerror = () => reject(new Error('Could not read image.'));
    reader.readAsDataURL(file);
  });
}

function canvasToBlob(canvas, contentType, quality = 0.82) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) =>
        result ? resolve(result) : reject(new Error('Could not convert image.')),
      contentType,
      quality
    );
  });
}

function replaceExtension(name, extension) {
  const basename = name.replace(/\.[^/.]+$/, '') || 'image';
  return `${basename}.${extension}`;
}

function getOutputSettings() {
  return { contentType: 'image/jpeg', extension: 'jpg', quality: 0.8 };
}

function drawScaledImage(image, maxSize) {
  const scale = Math.min(1, maxSize / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.round(image.naturalWidth * scale);
  const height = Math.round(image.naturalHeight * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not prepare image canvas.');

  context.drawImage(image, 0, 0, width, height);
  return canvas;
}

function drawLetterboxedImage(image, width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not prepare image canvas.');

  context.fillStyle = 'black';
  context.fillRect(0, 0, width, height);

  const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
  const targetWidth = Math.round(image.naturalWidth * scale);
  const targetHeight = Math.round(image.naturalHeight * scale);
  const x = Math.round((width - targetWidth) / 2);
  const y = Math.round((height - targetHeight) / 2);

  context.drawImage(image, x, y, targetWidth, targetHeight);
  return canvas;
}

export async function prepareImageForUpload(file, options = {}) {
  if (!isSupportedImageFile(file)) throw new Error('Please choose an image file.');

  if (file.type === 'image/png') return file;

  const image = await loadImage(file);
  const width = options.width || options.maxSize || 1600;
  const height = options.height || options.maxSize || 1600;
  const maxDimension = Math.max(width, height);
  const imageMaxDimension = Math.max(image.naturalWidth, image.naturalHeight);

  if (imageMaxDimension <= maxDimension) return file;

  const canvas = options.letterbox
    ? drawLetterboxedImage(image, width, height)
    : drawScaledImage(image, maxDimension);
  const output = getOutputSettings();
  const blob = await canvasToBlob(canvas, output.contentType, output.quality);

  return new File([blob], replaceExtension(file.name, output.extension), {
    type: output.contentType,
  });
}
